import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { awardsOverviewQueryOptions } from '@/api/awardsQueryOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import { useColumnState } from '@/components/hooks/useColumnState'
import useGridPageSize from '@/components/hooks/useGridPageSize'
import { usePersistedVisibilityModel } from '@/components/hooks/usePersistedVisibilityModel'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import { exportBlob } from '@/lib/exportUtils'
import { Box } from '@mui/material'
import {
    GridColDef,
    GridColumnVisibilityModel,
    GridCsvGetRowsToExportParams,
    gridExpandedSortedRowEntriesSelector,
    GridLogicOperator
} from '@mui/x-data-grid'
import { DataGridPremium, GridExcelExportOptions, GridExceljsProcessInput, GridInitialState, GridPivotModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { t } from 'i18next'
import React from 'react'

export const Route = createFileRoute('/_auth/awards')({
    component: AwardsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function AwardsComponent() {
    // const sessionOrganization = useSelector(getOrganization);

    const awardsQuery = useSuspenseQuery(awardsOverviewQueryOptions())
    const awardsOverview = awardsQuery.data

    // const navigate = useNavigate();

    const organizations = [
        ...new Set(awardsOverview.map((awa) => awa.currentOrganizationName)),
    ].sort()
    const awards = [
        ...new Set(awardsOverview.map((maw) => maw.name)),
    ].sort()

    const { xsmall, small, medium, large, xlarge } = useResponsive()
    const basicGridColumns = useBasicGridColumns()

    const columns: GridColDef[] = []
    columns.push({
        field: 'member',
        headerName: 'Mitglied',
        valueGetter: (_value, row) =>
            `${row.scoutId} - ${row.firstname} ${row.lastname}`,
        renderCell: (params) => {
            if (params.rowNode.type === 'group') {
                return params.value;
            }
            return (
                <Link
                    to="/member/$memberUuid/main"
                    params={{
                        memberUuid: params.row.memberUuid,
                    }}
                    preload="intent"
                >
                    {params.row.scoutId} - {params.row.firstname} {params.row.lastname}
                </Link>
            )
        },
    })
    if (xsmall || small || medium) {
        // noop
    } else {
        columns.push(
            {
                field: 'scoutId',
                headerName: 'Scout-ID',
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        return params.value;
                    }
                    return (
                        <Link
                            to="/member/$memberUuid/main"
                            params={{
                                memberUuid: params.row.memberUuid,
                            }}
                            preload="intent"
                        >
                            {params.row.scoutId}
                        </Link>
                    )
                },
            },
            {
                field: 'firstname',
                headerName: 'Vorname',
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        return params.value;
                    }
                    return (
                        <Link
                            to="/member/$memberUuid/main"
                            params={{
                                memberUuid: params.row.memberUuid,
                            }}
                            preload="intent"
                        >
                            {params.row.firstname}
                        </Link>
                    )
                },
            },
            {
                field: 'lastname',
                headerName: 'Nachname',
                renderCell: (params) => {
                    if (params.rowNode.type === 'group') {
                        return params.value;
                    }
                    return (
                        <Link
                            to="/member/$memberUuid/main"
                            params={{
                                memberUuid: params.row.memberUuid,
                            }}
                            preload="intent"
                        >
                            {params.row.lastname}
                        </Link>
                    )
                },
            },
        )
    }
    columns.push(
        {
            field: 'currentOrganization',
            headerName: t('property.membership.currentOrganization'),
            type: 'singleSelect',
            valueOptions: organizations,
            valueGetter: (_value, row) => `${row?.currentOrganizationName || ''}`,
        },
        {
            field: 'emailPrimary',
            headerName: t('property.member.emailPrimary'),
        },
        {
            field: 'name',
            headerName: t('entity.award'),
            type: 'singleSelect',
            valueOptions: awards,
            valueGetter: (_value, row) => `${row?.name || ''}`,
        },
        {
            field: 'awardingDate',
            headerName: t('property.memberAward.awardingDate'),
            type: 'date',
            valueGetter: (value) => value && new Date(value),
        },
    )
    if (large || xlarge) {
        columns.push(
            {
                ...basicGridColumns.descriptionColumn,
                editable: false
            }
        )
    }

    const initialVisibilityModel = {
        member: xsmall || small || medium,
        firstname: large || xlarge,
        lastname: large || xlarge,
        scoutId: large || xlarge,
        currentOrganization: large || xlarge,
        track: large || xlarge,
        step: true,
        completeDate: large || xlarge,
    } as GridColumnVisibilityModel

    const [visibilityModel, setVisibilityModel] = usePersistedVisibilityModel('awards', initialVisibilityModel)
    const handleVisibilityModelChange = React.useCallback((model: GridColumnVisibilityModel) => {
        setVisibilityModel(model)
    }, [setVisibilityModel]);

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('awards', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('awards', 35));

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('awardsOverview', apiRef, columns)

    const pivotModel: GridPivotModel = {
        rows: [{ field: 'member' }],
        columns: [{ field: 'name' }],
        values: [
            { field: 'awardingDate', aggFunc: 'min' },
        ],
    };

    const initialState: GridInitialState = {
        pivoting: {
            enabled: false,
            model: pivotModel,
        },
    }

    const exceljsPostProcess = async ({ worksheet }: GridExceljsProcessInput) => {
        // add a text after the data
        for (let i = worksheet.rowCount; i >= 2; i--) {
            const memberCell = worksheet.getCell(`A${i}`);
            if (!memberCell.value || memberCell.value === '') {
                worksheet.spliceRows(i, 1);
            }
        }
    };

    const excelOptions: GridExcelExportOptions = {
        exceljsPostProcess,
    };

    const exportEmail = ({ apiRef }: GridCsvGetRowsToExportParams) => {
        const rowEntries = gridExpandedSortedRowEntriesSelector(apiRef)
        const emails = new Set(rowEntries.map(entry => entry.model).map(award => award.emailPrimary).filter(email => email && email !== ''))
        const text = Array.from(emails).join(';')
        const blob = new Blob([text], {
            type: 'text/plain;charset=utf-8',
        });
        exportBlob(blob, 'emails.txt')
    }

    if (awardsQuery.isLoading || awardsQuery.isFetching || !awardsOverview) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <DataGridPremium
                    columns={columnsState.columns}
                    onColumnWidthChange={columnsState.onColumnWidthChange}
                    onColumnOrderChange={columnsState.onColumnOrderChange}
                    rows={awardsOverview}
                    getRowId={(row) => {
                        return row.uuid || ''
                    }}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model)
                        gridPageSize.handlePaginationModelChange('awards', model)
                    }}
                    columnVisibilityModel={visibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="awards" enablePivoting={true} enableEmailExport={true} exportEmail={exportEmail} /> } }}
                    slotProps={{
                        filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                            sx: { maxWidth: '100vw' }
                        },
                        toolbar: {
                            csvOptions: {
                                disableToolbarButton: true
                            },
                            printOptions: {
                                disableToolbarButton: true
                            },
                            excelOptions
                        }
                    }}
                    initialState={initialState}
                    hideFooter={true}
                    disableDensitySelector
                    disableColumnPinning
                    disableColumnReorder
                    density="compact"
                    showToolbar />
            </div>
        </Box>
    );
}
